/**
 * Short blurbs for natal-chart entities (Western tradition).
 *
 * One short title + a 1–3 sentence description, kept deliberately tight so
 * the in-chart popup stays readable in a Telegram WebView at phone width.
 */

export interface GlossaryEntry {
  title: string;
  subtitle?: string;
  body: string;
}

export const SIGN_GLOSSARY: Record<string, GlossaryEntry> = {
  Aries: {
    title: 'Aries',
    subtitle: 'Cardinal · Fire · ruled by Mars',
    body: 'The initiator. Aries energy is fast, brave and self-asserting — it begins things before knowing how they end.',
  },
  Taurus: {
    title: 'Taurus',
    subtitle: 'Fixed · Earth · ruled by Venus',
    body: 'The steady builder. Taurus values comfort, beauty and what lasts; it moves slowly and refuses to be rushed.',
  },
  Gemini: {
    title: 'Gemini',
    subtitle: 'Mutable · Air · ruled by Mercury',
    body: 'The connector. Gemini collects ideas, languages and contacts, and thrives on quick exchange across many topics.',
  },
  Cancer: {
    title: 'Cancer',
    subtitle: 'Cardinal · Water · ruled by the Moon',
    body: 'The keeper. Cancer protects what it loves — family, memory, home — and feels the emotional weather around it.',
  },
  Leo: {
    title: 'Leo',
    subtitle: 'Fixed · Fire · ruled by the Sun',
    body: 'The radiant self. Leo wants to express, create and be seen; warmth and pride are its native register.',
  },
  Virgo: {
    title: 'Virgo',
    subtitle: 'Mutable · Earth · ruled by Mercury',
    body: 'The craftsperson. Virgo refines, edits and serves — turning rough material into something useful and clean.',
  },
  Libra: {
    title: 'Libra',
    subtitle: 'Cardinal · Air · ruled by Venus',
    body: 'The diplomat. Libra weighs perspectives, seeks balance and beauty in relationship, and dislikes anything crude.',
  },
  Scorpio: {
    title: 'Scorpio',
    subtitle: 'Fixed · Water · ruled by Pluto (and Mars)',
    body: 'The transformer. Scorpio goes to the bottom of things — desire, power, loss — and is reborn through them.',
  },
  Sagittarius: {
    title: 'Sagittarius',
    subtitle: 'Mutable · Fire · ruled by Jupiter',
    body: 'The seeker. Sagittarius needs the horizon: travel, meaning, philosophy. It aims for the truth behind the fact.',
  },
  Capricorn: {
    title: 'Capricorn',
    subtitle: 'Cardinal · Earth · ruled by Saturn',
    body: 'The architect. Capricorn builds slowly toward a long goal — structure, responsibility and mastery in time.',
  },
  Aquarius: {
    title: 'Aquarius',
    subtitle: 'Fixed · Air · ruled by Uranus (and Saturn)',
    body: 'The outsider. Aquarius thinks in systems and futures, prizing freedom, fairness and the new over the inherited.',
  },
  Pisces: {
    title: 'Pisces',
    subtitle: 'Mutable · Water · ruled by Neptune (and Jupiter)',
    body: 'The dreamer. Pisces dissolves the line between self and world — through art, compassion or the mystical.',
  },
};

export const PLANET_GLOSSARY: Record<string, GlossaryEntry> = {
  sun: {
    title: 'Sun',
    subtitle: 'Identity · vitality',
    body: 'The core self — what you are becoming. The Sun shows how you shine, lead and find purpose.',
  },
  moon: {
    title: 'Moon',
    subtitle: 'Emotion · inner life',
    body: 'The private self — feelings, instincts, what soothes you. The Moon describes how you receive and remember.',
  },
  mercury: {
    title: 'Mercury',
    subtitle: 'Mind · communication',
    body: 'How you think, speak and learn. Mercury sets the speed of your thought and the style of your voice.',
  },
  venus: {
    title: 'Venus',
    subtitle: 'Love · values · beauty',
    body: 'What you find pleasurable and worth keeping. Venus colours how you love, attract and create harmony.',
  },
  mars: {
    title: 'Mars',
    subtitle: 'Drive · desire · will',
    body: 'How you act, fight for what you want and meet resistance. Mars is your engine and your edge.',
  },
  jupiter: {
    title: 'Jupiter',
    subtitle: 'Growth · meaning · luck',
    body: 'Where you expand, believe and find generosity. Jupiter opens doors but can also tempt you to overshoot.',
  },
  saturn: {
    title: 'Saturn',
    subtitle: 'Structure · time · discipline',
    body: 'Where life asks for patience and earned mastery. Saturn limits at first, then becomes your strongest backbone.',
  },
  uranus: {
    title: 'Uranus',
    subtitle: 'Awakening · freedom',
    body: 'The sudden break with what was. Uranus brings genius, rebellion and the courage to be different.',
  },
  neptune: {
    title: 'Neptune',
    subtitle: 'Dream · transcendence',
    body: 'Where boundaries dissolve — into art, mysticism, longing or illusion. Neptune asks for surrender.',
  },
  pluto: {
    title: 'Pluto',
    subtitle: 'Power · death · rebirth',
    body: 'Where you are forced through transformation. Pluto strips what is no longer real and returns deeper power.',
  },
  north_node: {
    title: 'North Node',
    subtitle: 'Soul direction',
    body: 'The growth edge of this life — the qualities you are being asked to develop, even if they feel unfamiliar.',
  },
  south_node: {
    title: 'South Node',
    subtitle: 'Inherited gift',
    body: 'What you already know how to do — easy, comfortable, sometimes a place you over-rely on.',
  },
  lilith: {
    title: 'Lilith',
    subtitle: 'The unruled self',
    body: 'The part of you that refuses to be tamed. Lilith marks where you guard autonomy fiercely.',
  },
  chiron: {
    title: 'Chiron',
    subtitle: 'The wounded healer',
    body: 'A core wound that becomes wisdom over time. You heal others through what you yourself had to face.',
  },
};

export const HOUSE_GLOSSARY: Record<number, GlossaryEntry> = {
  1: { title: '1st House', subtitle: 'Self · body · appearance', body: 'How you arrive in a room — your visible style, first reactions and the lens you wear into the world.' },
  2: { title: '2nd House', subtitle: 'Resources · self-worth', body: 'Your money, possessions and inner sense of value. What you produce and what you let yourself receive.' },
  3: { title: '3rd House', subtitle: 'Mind · siblings · short trips', body: 'Daily thinking, conversation, learning and the immediate neighbourhood of your life.' },
  4: { title: '4th House', subtitle: 'Home · roots · family', body: 'The private foundation — where you come from, what you call home, and the inner ground you stand on.' },
  5: { title: '5th House', subtitle: 'Creativity · romance · children', body: 'Play, self-expression, falling in love, art and the joy of creating something purely yours.' },
  6: { title: '6th House', subtitle: 'Work · service · health', body: 'Daily routines, craft, the body and how you take care of small, repeating things.' },
  7: { title: '7th House', subtitle: 'Partnership · the other', body: 'Marriage, close partners, key collaborators — the mirror in which you meet yourself through someone else.' },
  8: { title: '8th House', subtitle: 'Depth · shared resources · transformation', body: 'Intimacy, taboo, inheritance and the parts of life that pass through death and return changed.' },
  9: { title: '9th House', subtitle: 'Vision · travel · belief', body: 'The horizon — higher study, philosophy, long journeys and the larger meaning you live by.' },
  10: { title: '10th House', subtitle: 'Career · public life', body: 'Vocation, reputation and the role you take on in the world. What you are publicly known for.' },
  11: { title: '11th House', subtitle: 'Community · friends · future', body: 'Tribe, networks, ideals and the future you are walking toward together with others.' },
  12: { title: '12th House', subtitle: 'Inner world · retreat · the unseen', body: 'Solitude, the unconscious, hidden compassion and the things dissolved out of ordinary view.' },
};

export interface AspectGlossaryEntry extends GlossaryEntry {
  glyph: string;
  angle: number;
  nature: 'harmonious' | 'tense' | 'neutral';
  major: boolean;
}

export const ASPECT_GLOSSARY: Record<string, AspectGlossaryEntry> = {
  conjunction: {
    title: 'Conjunction',
    subtitle: '0° · major · blending',
    body: 'Two planets occupy the same point of the zodiac and fuse their energies into one. The flavour depends on which planets meet — it can be powerful, intensified or overwhelming.',
    glyph: '☌', angle: 0, nature: 'neutral', major: true,
  },
  sextile: {
    title: 'Sextile',
    subtitle: '60° · major · opportunity',
    body: 'A friendly opening between planets in compatible elements. Sextiles offer talent and ease, but they only flower if you choose to act on them.',
    glyph: '✶', angle: 60, nature: 'harmonious', major: true,
  },
  square: {
    title: 'Square',
    subtitle: '90° · major · friction',
    body: 'A 90° tension between planets that pulls you into action. Squares feel uncomfortable, but they are the muscle of growth — the place real character is forged.',
    glyph: '□', angle: 90, nature: 'tense', major: true,
  },
  trine: {
    title: 'Trine',
    subtitle: '120° · major · flow',
    body: 'A 120° harmony between planets in the same element. Trines give natural gifts and grace; the only risk is taking them for granted.',
    glyph: '△', angle: 120, nature: 'harmonious', major: true,
  },
  opposition: {
    title: 'Opposition',
    subtitle: '180° · major · polarity',
    body: 'Two planets face each other across the chart, holding opposite needs. The work is integration — neither side wins, both must be honoured.',
    glyph: '☍', angle: 180, nature: 'tense', major: true,
  },
  quincunx: {
    title: 'Quincunx',
    subtitle: '150° · minor · adjustment',
    body: 'A 150° angle between planets with nothing in common — different element and modality. It asks for constant fine-tuning rather than a clean resolution.',
    glyph: '⚻', angle: 150, nature: 'tense', major: false,
  },
  semisextile: {
    title: 'Semisextile',
    subtitle: '30° · minor · subtle link',
    body: 'A 30° whisper between neighbouring signs. The connection is mild — supportive but easy to overlook.',
    glyph: '⚺', angle: 30, nature: 'neutral', major: false,
  },
};

export const ANGLE_GLOSSARY: Record<string, GlossaryEntry> = {
  asc: {
    title: 'Ascendant (ASC)',
    subtitle: '1st-house cusp · rising sign',
    body: 'The horizon line at your birth. The Ascendant shapes your first impression, body style and the way life first meets you.',
  },
  dc: {
    title: 'Descendant (DC)',
    subtitle: '7th-house cusp',
    body: 'The opposite of your Ascendant — qualities you meet through others. Often what you seek in partnership.',
  },
  mc: {
    title: 'Midheaven (MC)',
    subtitle: '10th-house cusp',
    body: 'The highest point of your chart — your public direction, calling and how you appear to the world at large.',
  },
  ic: {
    title: 'Imum Coeli (IC)',
    subtitle: '4th-house cusp',
    body: 'The deepest point — your private roots, family inheritance and the soil from which everything else grows.',
  },
};
